import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Calendar, Pencil, Trash2, ExternalLink, MapPin } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { FOCUS_AREAS } from "@/lib/focusAreas";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type EventKind = "campaign" | "event" | "training";

type EventRow = {
  id: string;
  partner_org_id: string;
  title: string;
  description: string | null;
  location: string | null;
  url: string | null;
  starts_at: string;
  ends_at: string | null;
  focus_area: string | null;
  image_url: string | null;
  published: boolean;
  kind: EventKind;
  created_at: string;
};

const KIND_LABELS: Record<EventKind, string> = {
  campaign: "Campaign",
  event: "Event",
  training: "Training",
};


const toLocalInput = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 16);
};

export default function EventsTab({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<EventRow | null>(null);

  const list = useQuery({
    queryKey: ["partner-events", orgId],
    queryFn: async (): Promise<EventRow[]> => {
      const { data, error } = await supabase
        .from("partner_events")
        .select("*")
        .eq("partner_org_id", orgId)
        .order("starts_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as EventRow[];
    },
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("partner_events").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Event removed");
      qc.invalidateQueries({ queryKey: ["partner-events", orgId] });
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to remove"),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Post events your organization is hosting. Published events appear on your public partner profile.
        </p>
        <Button onClick={() => { setEditing(null); setOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" /> Add event
        </Button>
      </div>

      {list.isLoading ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (list.data ?? []).length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          No events yet. Add a community gathering, training, or webinar.
        </Card>
      ) : (
        <div className="grid gap-3">
          {list.data!.map((e) => {
            const start = new Date(e.starts_at);
            return (
              <Card key={e.id} className="p-4 flex items-start gap-4">
                <div className="shrink-0 w-14 text-center">
                  <div className="text-[10px] font-semibold tracking-wider uppercase text-primary">
                    {start.toLocaleString(undefined, { month: "short" })}
                  </div>
                  <div className="font-display text-2xl font-bold leading-tight">
                    {start.getDate()}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {start.toLocaleString(undefined, { weekday: "short" })}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-medium text-sm truncate">{e.title}</h3>
                    <Badge className="text-[10px]">{KIND_LABELS[e.kind] ?? "Event"}</Badge>
                    {e.focus_area && <Badge variant="secondary" className="text-[10px]">{e.focus_area}</Badge>}
                    {e.published ? (
                      <Badge variant="outline" className="text-[10px]">Published</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] opacity-60">Draft</Badge>
                    )}
                  </div>

                  {e.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{e.description}</p>}
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground mt-1">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {start.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                    </span>
                    {e.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> {e.location}
                      </span>
                    )}
                    {e.url && (
                      <a href={e.url} target="_blank" rel="noopener noreferrer" className="text-primary inline-flex items-center gap-1 hover:underline">
                        <ExternalLink className="h-3 w-3" /> Event link
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(e); setOpen(true); }}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => {
                    if (confirm(`Delete "${e.title}"?`)) del.mutate(e.id);
                  }}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <EventFormDialog
        open={open}
        onOpenChange={setOpen}
        orgId={orgId}
        editing={editing}
        onSaved={() => qc.invalidateQueries({ queryKey: ["partner-events", orgId] })}
      />
    </div>
  );
}

function EventFormDialog({
  open, onOpenChange, orgId, editing, onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  orgId: string;
  editing: EventRow | null;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(editing?.title ?? "");
  const [description, setDescription] = useState(editing?.description ?? "");
  const [location, setLocation] = useState(editing?.location ?? "");
  const [url, setUrl] = useState(editing?.url ?? "");
  const [startsAt, setStartsAt] = useState(toLocalInput(editing?.starts_at ?? null));
  const [endsAt, setEndsAt] = useState(toLocalInput(editing?.ends_at ?? null));
  const [focusArea, setFocusArea] = useState(editing?.focus_area ?? "");
  const [kind, setKind] = useState<EventKind>(editing?.kind ?? "event");
  const [published, setPublished] = useState(editing?.published ?? false);

  // Reset when editing changes
  useState(() => {});
  if (open && editing && title === "" && editing.title) {
    setTitle(editing.title);
    setDescription(editing.description ?? "");
    setLocation(editing.location ?? "");
    setUrl(editing.url ?? "");
    setStartsAt(toLocalInput(editing.starts_at));
    setEndsAt(toLocalInput(editing.ends_at));
    setFocusArea(editing.focus_area ?? "");
    setKind(editing.kind ?? "event");
    setPublished(editing.published);
  }

  const save = useMutation({
    mutationFn: async () => {
      if (!title.trim()) throw new Error("Title is required");
      if (!startsAt) throw new Error("Start date is required");
      const payload = {
        partner_org_id: orgId,
        title: title.trim(),
        description: description.trim() || null,
        location: location.trim() || null,
        url: url.trim() || null,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: endsAt ? new Date(endsAt).toISOString() : null,
        focus_area: focusArea || null,
        kind,
        published,
      };
      if (editing) {
        const { error } = await supabase.from("partner_events").update(payload).eq("id", editing.id);
        if (error) throw error;
      } else {
        const { data: u } = await supabase.auth.getUser();
        const { error } = await supabase.from("partner_events").insert({ ...payload, created_by: u.user?.id });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success(editing ? "Event updated" : "Event added");
      onSaved();
      onOpenChange(false);
      setTitle(""); setDescription(""); setLocation(""); setUrl("");
      setStartsAt(""); setEndsAt(""); setFocusArea(""); setKind("event"); setPublished(false);
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to save"),
  });


  return (
    <Dialog open={open} onOpenChange={(v) => {
      onOpenChange(v);
      if (!v) {
        setTitle(""); setDescription(""); setLocation(""); setUrl("");
        setStartsAt(""); setEndsAt(""); setFocusArea(""); setKind("event"); setPublished(false);
      }
    }}>

      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit event" : "Add event"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Type</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as EventKind)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="campaign">Campaign</SelectItem>
                <SelectItem value="event">Event</SelectItem>
                <SelectItem value="training">Training</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Starts</Label>
              <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
            </div>
            <div>
              <Label>Ends (optional)</Label>
              <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Location</Label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Mobile, AL or Virtual" />
            </div>
            <div>
              <Label>Focus area</Label>
              <Select value={focusArea || "none"} onValueChange={(v) => setFocusArea(v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {FOCUS_AREAS.map((f) => (
                    <SelectItem key={f} value={f}>{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Event link (optional)</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" />
          </div>
          <label className="flex items-center justify-between gap-3 text-sm pt-1">
            <span>Publish to public partner profile</span>
            <Switch checked={published} onCheckedChange={setPublished} />
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {editing ? "Save changes" : "Add event"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}